import React, { createContext } from "react";
import { connect } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { I18n } from "@aws-amplify/core";
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
} from "aws-amplify/auth";
import socketIOClient from "socket.io-client";
import Mousetrap from "mousetrap";
import { get, includes, forEach, toLower, join, slice } from "lodash";
import withWidth from "@material-ui/core/withWidth";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";

import ErrorBoundary from "./Common/ErrorBoundary";
import Loader from "./Common/Loader";
import Router from "./Router";
import SideBar from "./SideBar/SideBar";
import TopBar from "./TopBar/TopBar";
import PoweredBy from "./Footer/PoweredBy";
import LiveAssistanceDialogue from "./Communication/LiveAssistanceDialogue";
import VMIMobileTopBar from "./VMI/VMIMobileTopBar";
import Dict from "../resources/Dict";
import { fetchGet } from "../api/fetch";
import { API_DATASTORE } from "../constants/api";
import { human, warning, denied } from "../constants/common";
import { driveInOut, walkInOut } from "../constants/reducers";
import { WEB_SERVER_PORT, NODE_JS_LOCAL } from "../client-config";
import { setUser } from "../actions/user";
import { setError, enablePowerUserMode } from "../actions/common";
import { initiate, addEvent } from "../actions/siteEntranceExit/liveEvents";
import { getLov } from "../utils/lov";
import { fetchAttributes } from "../utils/attributes";
import { getAccessibleCompanies } from "../utils/companies";
import { roles } from "../constants/roles";
import {
  getAcceptances,
  getPolicies,
  getPublishers,
  processPoliciesStatus,
} from "../actions/policies";
import { getAttributeValue } from "../utils/profile";

let socket;

export const DialogueContext = createContext(null);

class Landing extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      keepMeLoggedInHandled: true, //had to always allow login for redirection
      initialized: false,
      showLiveAssistanceDialogue: false,
    };
  }

  async componentDidMount() {
    I18n.setLanguage("en");
    I18n.putVocabularies(Dict);
    this.interval = setInterval(() => { this.setState({ url: window.location.pathname }) }, 1000);

    if (
      !this.state.keepMeLoggedInHandled &&
      this.props.keepMeLoggedIn !== undefined
    ) {
      this.handleKeepMeLoggedIn();
    }

    try {
      const user = await getCurrentUser();
      const { tokens } = await fetchAuthSession();
      const jwtToken = tokens?.idToken?.toString?.() || "";
      const attributes = await fetchUserAttributes();

      let languages = await getLov(35);
      let profile = await fetchGet("profiles/me");
      this.setState({
        profileID: profile.id
      })
      let dbUser = await fetchGet("users/me");

      const firstName = getAttributeValue(profile, 31);
      const lastName = getAttributeValue(profile, 32);

      const assignedCompany = get(profile, ["company_id"]);
      const assignedSite = get(profile, ["site_id"]);

      let languageId = getAttributeValue(profile, 35);
      let language = getAttributeValue(profile, 35, { 35: languages }, "en");
      language = toLower(join(slice(language, 0, 2), "")) || "en";

      // Set dictionnary and language
      I18n.setLanguage(language);

      // Get sys keys from datastore
      let defaultTimer = await this.getCleaningTimer();

      // Get potential matches threshold from datastore
      let potentialMatchesThreshold = await this.getPotentialMatchesThreshold();

      // Get solutions access from datastore
      let solutionsAccess = await this.getSolutionsAccess();

      // Get NFR enabled from datastore
      let nfrEnabled = await this.getNfrEnabled();

      // Handle Power User Mode
      this.handlePowerUserMode();

      // Fetch Attributes
      fetchAttributes();

      // Role level
      let roleLevels = {
        [roles.jrvAdmin]: false,
        [roles.customerAdmin]: false,
        [roles.order]: false,
        [roles.requester]: false,
        [roles.other]: false,
        [roles.user]: false,
      };
      forEach(get(dbUser, ["roles"]), (r) => {
        if (includes(toLower(get(r, ["name_en"])), "jrv admin")) {
          roleLevels[roles.jrvAdmin] = true;
        } else if (includes(toLower(get(r, ["name_en"])), "customer admin")) {
          roleLevels[roles.customerAdmin] = true;
        } else if (includes(toLower(get(r, ["name_en"])), "order user")) {
          roleLevels[roles.order] = true;
        } else if (includes(toLower(get(r, ["name_en"])), "order requester")) {
          roleLevels[roles.requester] = true;
        } else if (includes(toLower(get(r, ["name_en"])), "order viewer") || includes(toLower(get(r, ["name_en"])), "user")) {
          roleLevels[roles.user] = true;
        } else {
          roleLevels[roles.other] = true;
        }
      });
      const roleLevel = roleLevels[roles.jrvAdmin] ?
        roles.jrvAdmin : roleLevels[roles.customerAdmin] ?
          roles.customerAdmin : roleLevels[roles.order] ?
            roles.order : roleLevels[roles.requester] ?
              roles.requester : roleLevels[roles.other] ?
                roles.other : roles.user;

      // Companies/Sites/Zones list depending on the permissions and assigned company/site
      const companies = await getAccessibleCompanies(
        get(dbUser, ["permissions"]),
        assignedCompany,
        assignedSite
      );

      this.props.dispatch(
        setUser({
          username: user.username,
          firstName,
          lastName,
          email: attributes.email,
          profile,
          language,
          languageId,
          jwtToken,
          defaultTimer,
          potentialMatchesThreshold,
          nfrEnabled,
          role: {
            roles: get(dbUser, ["roles"]),
            level: roleLevel,
          },
          id: get(dbUser, ["id"]),
          solutionsAccess: get(solutionsAccess, ["solutions"]),
          assignedCompany,
          assignedSite,
          companies,
          permissions: get(dbUser, ["permissions"]),
        })
      );
      this.setState({ initialized: true });
      this.monitorLiveEvents(defaultTimer);
      this.dataPolicies();
    } catch (e) {
      console.log(e);
      this.props.dispatch(
        setError({
          error: {
            msg: I18n.get("error_failedLoadProfile"),
            level: 1,
          },
        })
      );
    }
  }

  handleKeepMeLoggedIn = () => {
    if (!this.props.keepMeLoggedIn) {
      // Clear local storage when the tab/window is closed
      // so the user does not remain logged in
      window.addEventListener("unload", function () {
        localStorage.clear();
      });
    }
    this.setState({ keepMeLoggedInHandled: true });
  };

  monitorLiveEvents = async (defaultTimer) => {
    // Initial config of websocket
    console.debug("Landing >> Initiating web socket connection to server.")
    if (NODE_JS_LOCAL) {
      socket = socketIOClient(`:${WEB_SERVER_PORT}`);
    } else {
      socket = socketIOClient();
    }

    socket.on("connect", () => {
      console.debug("Landing >> Web socket connected.");
    });

    socket.on("connect_error", (err) => {
      console.debug(`Landing >> Unable to connect web socket ERROR: ${JSON.stringify(err)}`);
    })

    socket.on("disconnect", () => {
      console.debug("Landing >> Web Socket disconnected.")
    });

    // Initial state of live events
    await this.props.dispatch(initiate(driveInOut, defaultTimer));
    await this.props.dispatch(initiate(walkInOut, defaultTimer));

    // Listen for incoming events
    socket.on("eventDetected", async (data) => {
      console.log(
        `eventDetected [${data.human ? human : data.type}] [${data.status}] [${data.state}]`,
        data
      );
      if (data.zone_id && data.type) {
        this.props.dispatch(
          addEvent(
            data.human ? walkInOut : driveInOut,
            data.zone_id,
            data.type,
            data
          )
        );
      }
    });
  };

  dataPolicies = async () => {
    await this.props.dispatch(getPublishers());
    await this.props.dispatch(getPolicies());
    await this.props.dispatch(getAcceptances());
    await this.props.dispatch(processPoliciesStatus());

    if (
      includes(
        [warning, denied],
        get(this.props, ["policies", "status", "access"])
      )
    ) {
      this.setState({
        policyAlert: true,
      });
    }
  };

  getCleaningTimer = async () => {
    try {
      let res = await fetchGet("ds/sys/dashboardCleaningTimer", API_DATASTORE);
      if (res.data && res.data.length > 0) {
        return res.data[0].value;
      }
    } catch (e) {
      console.log(e);
      return 0;
    }
  };

  getPotentialMatchesThreshold = async () => {
    try {
      let res = await fetchGet(
        "ds/sys/potentialMatchesThreshold",
        API_DATASTORE
      );
      return get(res, ["data", 0, "value"]);
    } catch (e) {
      console.log(e);
      return 0;
    }
  };

  getNfrEnabled = async () => {
    try {
      let res = await fetchGet("ds/sys/nfrEnabled", API_DATASTORE);
      return get(res, ["data", 0, "value"]);
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  getSolutionsAccess = async () => {
    try {
      let res = await fetchGet("ds/sys/solutionsAccess", API_DATASTORE);
      return get(res, ["data", 0, "value"]);
    } catch (e) {
      console.log(e);
      return {
        solutions: {
          MDC: {
            allow: false,
          },
          HSP: {
            allow: false,
          },
        },
      };
    }
  };

  handlePowerUserMode = () => {
    Mousetrap.bind("p o w e r", () => {
      window.alert("Power User Mode Enabled");
      this.props.dispatch(enablePowerUserMode(true));
    });
  };

  render() {
    if (this.props.error && this.props.error.level === 1) {
      return (
        <Box style={{ marginTop: "25%", width: "500px", margin: "auto" }}>
          <h2>{I18n.get("error_errorEncountered")}</h2>
          {this.props.error.msg}
        </Box>
      );
    }
    if (!this.state.initialized) {
      return (
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            height: "100vh",
          }}
        >
          <Loader noDecoration />
        </Box>
      );
    }

    // LiveAssistanceDialogue Context
    const setShowLiveAssistanceDialogue = () => {
      this.setState({ showLiveAssistanceDialogue: !this.state.showLiveAssistanceDialogue })
    }

    function getWidth() {
      return Math.max(
        document.body.scrollWidth,
        document.documentElement.scrollWidth,
        document.body.offsetWidth,
        document.documentElement.offsetWidth,
        document.documentElement.clientWidth
      );
    }

    // Desktop Display
    return (
      <DialogueContext.Provider value={{ showLiveAssistanceDialogue: this.state.showLiveAssistanceDialogue, setShowLiveAssistanceDialogue }}>
        <BrowserRouter>
          <Grid container direction="column" className="height-100" wrap="nowrap" >
            <Grid item style={{ zIndex: 4 }}>
              <TopBar />
              {getWidth() > 470 ? null : <VMIMobileTopBar />}
              <LiveAssistanceDialogue />
            </Grid>
            <Grid item xs className="overflow-auto" >
              <Grid container className="height-100" wrap="nowrap">
                <Grid item style={{ zIndex: 2 }}>
                  <SideBar />
                </Grid>
                <Grid item xs className="overflow-auto">
                  <Grid
                    container
                    direction="column"
                    className="height-100"
                    wrap="nowrap"
                  >
                    <Grid item xs className="overflow-auto">
                      <ErrorBoundary>
                        <Router
                          mobile={false}
                          profileID={this.state.profileID}
                          userRole={get(this.props, ["user", "role"])}
                          policyAccessDenied={
                            get(this.props, ["policies", "status", "access"]) ===
                            denied
                          }
                        />
                      </ErrorBoundary>
                    </Grid>
                    {includes(
                      ["warning", "denied"],
                      get(this.props, ["policies", "status", "access"])
                    ) && (
                        <Grid item>
                          <Snackbar
                            open={this.state.policyAlert}
                            anchorOrigin={{ vertical: "top", horizontal: "center" }}
                          >
                            <Alert
                              severity={
                                get(this.props, [
                                  "policies",
                                  "status",
                                  "access",
                                ]) === denied
                                  ? "error"
                                  : "warning"
                              }
                              onClose={() => this.setState({ policyAlert: false })}
                            >
                              {I18n.get(
                                get(this.props, [
                                  "policies",
                                  "status",
                                  "access",
                                ]) === denied
                                  ? "profiles_policyAlertDenied"
                                  : "profiles_policyAlertWarning"
                              )}
                            </Alert>
                          </Snackbar>
                        </Grid>
                      )}
                    <Grid item>
                      <PoweredBy whiteBG={true} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </BrowserRouter>
      </DialogueContext.Provider>

    );
  }
}

const mapStateToProps = (state) => {
  return {
    keepMeLoggedIn: state.common.keepMeLoggedIn,
    error: state.common.error,
    user: state.user,
    policies: state.policies,
  };
};

export default withWidth()(connect(mapStateToProps)(Landing));
